// Импорт из Zepp Life (zip или BODY_*.csv); незнакомый CSV — сопоставление колонок с предпросмотром.

import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { Button, ErrorText, Input, Modal, Select } from '@/components/ui'
import { pluralRu } from '@/lib/format'
import { metricsApi, type CsvMapping, type CsvPreview, type DateFormat, type ImportResult } from '@/pages/metrics/api'

import { fieldError, formError, useInvalidateMetrics } from './helpers'

const DATE_FORMATS: { value: DateFormat; label: string }[] = [
  { value: 'auto', label: 'Определить само' },
  { value: 'iso', label: 'ГГГГ-ММ-ДД [чч:мм]' },
  { value: 'dmy', label: 'ДД.ММ.ГГГГ [чч:мм]' },
  { value: 'mdy', label: 'ММ/ДД/ГГГГ [чч:мм]' },
  { value: 'unix', label: 'Unix-время, секунды' },
  { value: 'unix_ms', label: 'Unix-время, миллисекунды' },
]

const MAX_SHOWN_ERRORS = 50

function ResultView({ result }: { result: ImportResult }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 tabular-nums">
        <dt className="text-muted">Строк загружено</dt>
        <dd className="text-right font-semibold">{result.rows_ok}</dd>
        <dt className="text-muted">Новых или изменённых записей</dt>
        <dd className="text-right font-semibold">{result.written}</dd>
        {result.rows_skipped ? (
          <>
            <dt className="text-muted">Пропущено (вес 0 или пусто)</dt>
            <dd className="text-right">{result.rows_skipped}</dd>
          </>
        ) : null}
        <dt className="text-muted">Строк с ошибками</dt>
        <dd className={result.rows_failed ? 'text-right font-semibold text-danger' : 'text-right'}>
          {result.rows_failed}
        </dd>
      </dl>
      {result.rows_ok > 0 && result.written === 0 ? (
        <p className="text-muted">Все записи уже были загружены раньше — дубликатов нет.</p>
      ) : null}
      {result.errors.length > 0 ? (
        <div>
          <p className="mb-1 font-medium">Не загружены:</p>
          <ul className="max-h-48 overflow-y-auto rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs">
            {result.errors.slice(0, MAX_SHOWN_ERRORS).map((e) => (
              <li key={`${e.row}-${e.message}`} className="py-0.5">
                <span className="font-medium tabular-nums">строка {e.row}</span>: {e.message}
              </li>
            ))}
          </ul>
          {result.errors.length > MAX_SHOWN_ERRORS ? (
            <p className="mt-1 text-xs text-muted">
              и ещё {result.errors.length - MAX_SHOWN_ERRORS}{' '}
              {pluralRu(result.errors.length - MAX_SHOWN_ERRORS, ['строка', 'строки', 'строк'])}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function PreviewTable({ preview }: { preview: CsvPreview }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs">
        <thead className="bg-surface-2 text-left">
          <tr>
            {preview.columns.map((c) => (
              <th key={c} className="whitespace-nowrap px-2 py-1.5 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {preview.columns.map((c, j) => (
                <td key={c} className="whitespace-nowrap px-2 py-1 tabular-nums">
                  {row[j] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MappingForm({
  file,
  preview,
  onDone,
}: {
  file: File
  preview: CsvPreview
  onDone: (result: ImportResult) => void
}) {
  const invalidate = useInvalidateMetrics()
  const [mapping, setMapping] = useState<CsvMapping>(
    preview.suggested ?? { date_col: '', date_format: 'auto', weight_col: '', body_fat_col: null },
  )
  const commit = useMutation({
    mutationFn: () => metricsApi.commitCsv(file, mapping),
    onSuccess: (result) => {
      void invalidate()
      onDone(result)
    },
  })
  const columns = preview.columns.map((c) => ({ value: c, label: c }))
  const set = (patch: Partial<CsvMapping>) => setMapping((m) => ({ ...m, ...patch }))
  const ready = Boolean(mapping.date_col && mapping.weight_col)

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (ready) commit.mutate()
      }}
      noValidate
    >
      <p className="text-sm text-muted">
        Формат не узнан. Покажи, где дата и вес — остальное сделаем сами. Первые строки файла (всего{' '}
        {preview.total_rows}):
      </p>
      <PreviewTable preview={preview} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Колонка с датой"
          value={mapping.date_col}
          placeholder="— выбери —"
          options={columns}
          onChange={(e) => set({ date_col: e.target.value })}
          error={fieldError(commit.error, 'date_col')}
        />
        <Select
          label="Формат даты"
          value={mapping.date_format}
          options={DATE_FORMATS}
          onChange={(e) => set({ date_format: e.target.value as DateFormat })}
        />
        <Select
          label="Колонка с весом, кг"
          value={mapping.weight_col}
          placeholder="— выбери —"
          options={columns}
          onChange={(e) => set({ weight_col: e.target.value })}
          error={fieldError(commit.error, 'weight_col')}
        />
        <Select
          label="Колонка с % жира"
          value={mapping.body_fat_col ?? ''}
          placeholder="— нет —"
          options={columns}
          onChange={(e) => set({ body_fat_col: e.target.value || null })}
          error={fieldError(commit.error, 'body_fat_col')}
        />
      </div>
      <ErrorText error={formError(commit.error, ['date_col', 'weight_col', 'body_fat_col'])} />
      <Button type="submit" variant="primary" loading={commit.isPending} disabled={!ready}>
        Загрузить
      </Button>
    </form>
  )
}

type Step =
  | { kind: 'file' }
  | { kind: 'mapping'; file: File; preview: CsvPreview }
  | { kind: 'result'; result: ImportResult }

function ImportFlow({ onClose }: { onClose: () => void }) {
  const invalidate = useInvalidateMetrics()
  const [step, setStep] = useState<Step>({ kind: 'file' })
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')

  const upload = useMutation({
    mutationFn: (f: File) => metricsApi.importZepp(f, password || null),
    onSuccess: (answer, f) => {
      if (answer.status === 'done') {
        void invalidate()
        setStep({ kind: 'result', result: answer.result })
      } else {
        setStep({ kind: 'mapping', file: f, preview: answer.preview })
      }
    },
  })

  if (step.kind === 'result') {
    return (
      <div className="flex flex-col gap-4">
        <ResultView result={step.result} />
        <Button variant="primary" onClick={onClose}>
          Готово
        </Button>
      </div>
    )
  }
  if (step.kind === 'mapping') {
    return <MappingForm file={step.file} preview={step.preview} onDone={(result) => setStep({ kind: 'result', result })} />
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (file) upload.mutate(file)
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <p className="text-sm text-muted">
        Подойдёт весь архив экспорта Zepp Life (.zip) или файл BODY_*.csv из его папки BODY. Другой CSV тоже
        можно — колонки сопоставишь сам. Повторная загрузка того же файла дубликатов не создаёт.
      </p>
      <Input
        label="Файл"
        type="file"
        accept=".zip,.csv,text/csv,application/zip"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        error={fieldError(upload.error, 'file')}
      />
      <Input
        label="Пароль архива"
        type="password"
        autoComplete="off"
        hint="Только если Zepp Life выдал архив с паролем."
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={fieldError(upload.error, 'password')}
      />
      <ErrorText error={formError(upload.error, ['file', 'password'])} />
      <Button type="submit" variant="primary" loading={upload.isPending} disabled={!file}>
        Загрузить
      </Button>
    </form>
  )
}

export function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title="Импорт из Zepp Life" className="sm:max-w-2xl">
      {open ? <ImportFlow onClose={onClose} /> : null}
    </Modal>
  )
}
